import { z } from "zod";
import { ownerTodoKindSchema, renewalReasonSchema } from "../schemas/home.ts";
import { clientFilterSchema } from "../schemas/clients.ts";
import { bookingEntrySchema } from "../schemas/booking.ts";
import {
  assistantActionTools,
  assistantCardKinds,
  assistantChartKinds,
  assistantCloseReasons,
  assistantGapKinds,
  assistantOutcomes,
  assistantRatingReasons,
  assistantTopics,
} from "../schemas/assistant.ts";
import { CAMPAIGN_TOKEN_MAX, heardFromValues } from "../signup-source.ts";
import { nameBands, nameSearchBoxes } from "../name-search.ts";
import { attendanceMethodSchema, bookingForSchema, classFillSchema, classLevelSchema, ownerNudgeKindSchema, packageFreezeReasonSchema } from "../enums.ts";
import { frontDeskItemSchema } from "../front-desk-access.ts";
import { businessModules } from "../business-modules.ts";

/**
 * The tracking plan: every product analytics event Teamna sends to Mixpanel.
 *
 * One entry per event. Every `track()` call on every surface (API, worker, Desk web, mobile, the
 * public pages) takes its name from here, and TypeScript checks the properties against the schema.
 * `tests/analytics-registry.test.ts` checks the other direction: each event here is sent from
 * somewhere, and no schema uses a key that can carry personal data.
 *
 * Spec: ai-personal-assistant `teamna/strategy/analytics-spec.md`. Skill: `.claude/skills/teamna-analytics`.
 *
 * Rules for a new event:
 * - Name is `object_action`, snake case, past tense (`booking_created`).
 * - Properties are ids, counts, enums, amounts in minor units, and booleans. Never a name, a phone,
 *   an email, or text that a person typed (a note, a message, a class name).
 * - `business_id` and `role` are added by the tracker from the context. Do not repeat them here.
 *   So are `booking_for` and `class_fill` (the business shape, migration 0069), so every report can
 *   break down by shape.
 */

/**
 * Who did it. A job or webhook with no person is `system`. `frontdesk` is the Front desk team role
 * (migration 0080, T1): it books, records payments, checks in and approves new clients.
 */
export const analyticsRoles = ["owner", "coowner", "coach", "frontdesk", "parent", "system"] as const;
export type AnalyticsRole = (typeof analyticsRoles)[number];

/** Where an event is sent from. */
export const analyticsSources = ["server", "desk_web", "mobile", "public_web"] as const;
export type AnalyticsSource = (typeof analyticsSources)[number];

/**
 * Keys that never appear in an event or profile. The CI test fails when a schema uses one, also as
 * a suffix (`child_name`, `parent_email`).
 */
export const forbiddenAnalyticsKeys = [
  "name",
  "full_name",
  "first_name",
  "last_name",
  "phone",
  "email",
  "note",
  "notes",
  "message",
  "body",
  "text",
  "address",
  "title",
  "label",
  // A referrer is a page address or, on the sign-up (task 0.7), the name of the owner who told them.
  "referrer",
] as const;

/** The only keys allowed although they end in a forbidden word: a business name is not personal data. */
export const allowedAnalyticsKeys = ["business_name"] as const;

type EventDefinition<P extends z.ZodRawShape, S extends readonly AnalyticsSource[]> = {
  /** One sentence: what happened. Pushed to Mixpanel Lexicon. */
  description: string;
  /** The product area, for grouping in Lexicon and in reports. */
  feature: string;
  sources: S;
  props: z.ZodObject<P>;
};

function define<P extends z.ZodRawShape, const S extends readonly AnalyticsSource[]>(
  definition: EventDefinition<P, S>,
): EventDefinition<P, S> {
  return definition;
}

const id = z.string().min(1);
const count = z.number().int().min(0);
/** Which packs a pause or an end took (D17): one pack, every pack of the account, or one child's packs. */
const freezeEventScope = z.enum(["pack", "all", "participant"]);
const amountMinor = z.number().int();
const currency = z.string().length(3);
const none = z.object({});

const signInMethod = z.enum(["phone", "email", "whatsapp", "email_link", "demo_link", "test_account"]);
const bookingActor = z.enum(["parent", "owner", "coowner", "coach", "frontdesk"]);
/** The role a team member gets: `coach` (staff), `coowner` (admin) or `frontdesk` (Front desk, T1). */
const teamRole = z.enum(["coach", "coowner", "frontdesk"]);
const checkoutKind = z.enum(["package", "payment_request", "owed_balance", "pay_link"]);
const paymentKind = z.enum(["package", "recurring_fee", "owed_balance", "one_off"]);
/** Who may buy a package: `new_clients` is an intro offer (`newClientsOnly`, migration 0070), `all` every other package. */
const packageAudience = z.enum(["new_clients", "all"]);
/**
 * What a payment or a package is (M2): `pack` a block of sessions, `membership` a monthly membership
 * (migration 0079), `intro` an intro offer (`newClientsOnly`, M1). `packageKindOf` in `membership.ts`.
 */
const packageKind = z.enum(["pack", "membership", "intro"]);

export const analyticsEvents = {
  // 5.1 Sign-in and identity
  user_signed_up: define({
    description: "A person signed in for the first time and Teamna created the user.",
    feature: "sign_in",
    sources: ["server"],
    props: z.object({ method: signInMethod }),
  }),
  user_signed_in: define({
    description: "A person finished a sign-in.",
    feature: "sign_in",
    sources: ["server"],
    props: z.object({ method: signInMethod, is_new_user: z.boolean() }),
  }),
  profile_completed: define({
    description: "A person gave their name for the first time.",
    feature: "sign_in",
    sources: ["server"],
    props: none,
  }),
  place_chosen: define({
    description: "A person opened one of their places (a business and a role).",
    feature: "account",
    sources: ["desk_web", "mobile"],
    props: z.object({
      source: z.enum(["choose_page", "account_menu", "auto"]),
      place_count: count,
    }),
  }),
  screen_viewed: define({
    description: "A screen opened. `screen` is the route pattern, never a URL with ids.",
    feature: "navigation",
    sources: ["desk_web", "mobile"],
    props: z.object({ screen: z.string().min(1).max(120) }),
  }),

  // 5.2 Owner: business setup
  business_created: define({
    description:
      "A person created a business. `owner_role` is what they picked as their role at it (a label only), none when not asked. `heard_from` is the answer to \"How did you hear about Teamna?\" (task 0.7), none when not asked. `utm_source`, `utm_medium` and `utm_campaign` are the website campaign that brought the person, when there was one. The referrer name that a person types is never sent.",
    feature: "setup",
    sources: ["server"],
    props: z.object({
      business_name: z.string().max(120),
      owner_role: z.enum(["owner", "manager", "head_coach", "none"]).optional(),
      heard_from: z.enum([...heardFromValues, "none"]).optional(),
      utm_source: z.string().max(CAMPAIGN_TOKEN_MAX).optional(),
      utm_medium: z.string().max(CAMPAIGN_TOKEN_MAX).optional(),
      utm_campaign: z.string().max(CAMPAIGN_TOKEN_MAX).optional(),
    }),
  }),
  business_shape_changed: define({
    description:
      "The business shape was set or changed (migration 0069): who books (`booking_for`) and how classes fill (`class_fill`). `source: start` is the owner sign-up on Desk (the shape the business starts with, sent once with business_created); `source: settings` is a change in Settings > Business. Every other event carries the shape in its context too.",
    feature: "setup",
    sources: ["server"],
    props: z.object({
      booking_for: bookingForSchema,
      class_fill: classFillSchema,
      source: z.enum(["start", "settings"]),
    }),
  }),
  front_desk_access_changed: define({
    description:
      "The owner or an admin saved Settings, Front desk and changed what the Front desk role may see and do. `turned_on` and `turned_off` are the items that changed in this save; `items_on` counts the items that are on after it.",
    feature: "team",
    sources: ["server"],
    props: z.object({
      turned_on: z.array(frontDeskItemSchema),
      turned_off: z.array(frontDeskItemSchema),
      items_on: count,
    }),
  }),
  module_switched: define({
    description:
      "The owner or an admin turned a module on or off in Settings, Modules. One event for each module that changed in the save. `module`: the module (`coaches_add_participants`). `on`: the state after the save.",
    feature: "team",
    sources: ["server"],
    props: z.object({ module: z.enum(businessModules), on: z.boolean() }),
  }),
  owner_welcome_sent: define({
    description:
      "Teamna sent the welcome to a new owner after their first business was created: a WhatsApp from the Teamna number, or an email.",
    feature: "setup",
    sources: ["server"],
    props: z.object({ channel: z.enum(["whatsapp", "email"]) }),
  }),
  todo_row_opened: define({
    description:
      "The owner opened a row of the To do list on Home (most rows do the job in place; `link_parents` opens New participants; `plan_next_month` opens the Timetable on the month to plan). `kind`: the job (`parent_answers`, `pending_members`, `sessions_to_decide`, `participants_to_confirm`, `link_parents`, `plan_next_month`). `count`: how many things wait in it. `position`: the row, 1 at the top. `job_count`: the rows on the list.",
    feature: "todo",
    sources: ["desk_web"],
    props: z.object({ kind: ownerTodoKindSchema, count, position: z.number().int().min(1), job_count: count }),
  }),
  call_booking_opened: define({
    description:
      "An owner opened the booking page for a call with the Teamna team (\"Book a call with us\"), from a setup page or the Home checklist. The booking itself happens in Google Calendar.",
    feature: "setup",
    sources: ["desk_web"],
    props: z.object({ source: z.enum(["setup_page", "home"]) }),
  }),
  first_run_step_finished: define({
    description:
      "A new owner left one step of the first run on Desk (after Create my business): the logo or the team. `outcome` says if they used it or pressed Skip.",
    feature: "setup",
    sources: ["desk_web"],
    props: z.object({
      step: z.enum(["logo", "team"]),
      outcome: z.enum(["done", "skipped"]),
    }),
  }),
  app_accent_changed: define({
    description:
      "An owner or admin changed the colour of the Teamna app for the business on Desk (Settings, Brand; mobile V2, slice 2.2). `choice` is Teamna blue, the logo colour or a colour of their own. `adjusted` is true when the app makes the colour darker so white text on it passes WCAG AA.",
    feature: "brand",
    sources: ["server"],
    props: z.object({
      choice: z.enum(["teamna", "logo", "custom"]),
      adjusted: z.boolean(),
    }),
  }),
  account_deleted: define({
    description:
      "A person deleted their own Teamna account (mobile V2, slice 0.2). The row stays without personal data, so business records keep a payer. `was_member` and `was_staff` say what they were at any business.",
    feature: "account",
    sources: ["server"],
    props: z.object({ was_member: z.boolean(), was_staff: z.boolean() }),
  }),
  desk_handoff_opened: define({
    description:
      "An owner on the mobile app opened Desk in the browser from a setup screen that moved to Desk (mobile V2, D1), or from a to-do item done on Desk (`owner_queue`). `from_screen` is that screen.",
    feature: "setup",
    sources: ["mobile"],
    props: z.object({
      /** `owner_queue`: a to-do item on the owner's home that is done on Desk (link names to parents). */
      from_screen: z.enum(["business", "team", "members_imported", "sessions", "timetable", "packages", "invite_sent", "placement", "balances", "owner_queue"]),
    }),
  }),
  setup_step_completed: define({
    description: "The owner finished one step of the business setup.",
    feature: "setup",
    sources: ["server"],
    props: z.object({
      step: z.enum(["team", "members", "classes", "timetable", "packages", "rules", "balances", "placement"]),
    }),
  }),
  class_created: define({
    description:
      "The owner added a class (a group). `open_to_all`: any participant can book it with a package for it, whatever their level. `class_kind`: a group class or a private class. `is_duet`: a private class for two (Studio S2). `level`: the level badge of the class; left out when it has none.",
    feature: "setup",
    sources: ["server"],
    props: z.object({
      group_id: id,
      source: z.enum(["setup", "catalog"]),
      open_to_all: z.boolean().optional(),
      class_kind: z.enum(["group", "private"]).optional(),
      is_duet: z.boolean().optional(),
      level: classLevelSchema.optional(),
    }),
  }),
  // Pilates studio shape, S2: kind, places and level of a class changed on the class panel.
  class_shape_changed: define({
    description:
      "The owner or an admin changed the kind, the places of a private class (one, or two for a duet) or the level of a class on the class panel. `changed`: what changed. `class_kind`, `is_duet` and `level` are the values after the change (`level` left out when the class has none).",
    feature: "setup",
    sources: ["server"],
    props: z.object({
      group_id: id,
      changed: z.array(z.enum(["kind", "places", "level"])).min(1),
      class_kind: z.enum(["group", "private"]),
      is_duet: z.boolean(),
      level: classLevelSchema.optional(),
    }),
  }),
  class_access_changed: define({
    description:
      "The owner turned \"Open to all\" on or off for a class. On: any participant can book it with a package for it, whatever their level (for example a training session for every level).",
    feature: "setup",
    sources: ["server"],
    props: z.object({ group_id: id, open_to_all: z.boolean() }),
  }),
  // Pilates studio shape, B3: booking rules for one class (migration 0073).
  class_rules_changed: define({
    description:
      "The owner or an admin saved the booking rules of one class on Desk (the class panel, \"Booking rules for this class\"). `max_book_ahead_days` and `cancel_cutoff_hours` are the rules that apply to the class after the save (its own, else the business values). `uses_business_default`: the class has no rules of its own now (Business default for both). `class_kind`: a group class or a private session.",
    feature: "setup",
    sources: ["server"],
    props: z.object({
      group_id: id,
      class_kind: z.enum(["group", "private"]),
      max_book_ahead_days: count,
      cancel_cutoff_hours: count,
      uses_business_default: z.boolean(),
    }),
  }),
  timetable_read: define({
    description:
      "The owner sent timetable photos or PDFs to the reader in setup. `simulated` is true when no reader is connected and the answer is the sport template.",
    feature: "timetable",
    sources: ["server"],
    props: z.object({ file_count: count, pdf_count: count, group_count: count, simulated: z.boolean() }),
  }),
  timetable_published: define({
    description: "The owner published one month of the weekly timetable (spec timetable-months.md). `source`: edited, or copied from the month before. `month_offset`: 0 this month, 1 the next month.",
    feature: "timetable",
    sources: ["server"],
    props: z.object({
      slots_added: count,
      slots_changed: count,
      slots_removed: count,
      sessions_cancelled: count,
      /** How the month started: edited, or copied from the month before. */
      source: z.enum(["editor", "month_copy"]),
      /** 0: this month, 1: the next month. */
      month_offset: count,
    }),
  }),
  timetable_month_started: define({
    description:
      "The owner started an empty month of the timetable (spec timetable-months.md): a copy of the month before, or empty. `month_offset`: 0 this month, 1 the next month.",
    feature: "timetable",
    sources: ["desk_web"],
    props: z.object({ choice: z.enum(["copy", "empty"]), month_offset: count }),
  }),
  timetable_level_picked: define({
    description:
      "The owner picked a level on the timetable to add its weekly times. `via`: the level list, or the Next level button (the next level with no times). `levels_with_times` and `level_count` are read before the pick.",
    feature: "timetable",
    sources: ["desk_web"],
    props: z.object({
      via: z.enum(["list", "next_button"]),
      level_kind: z.enum(["group", "private"]),
      level_count: count,
      levels_with_times: count,
    }),
  }),
  package_product_created: define({
    description:
      "The owner added a package that members can buy. `audience`: `new_clients` for an intro offer that only a new client can buy one time, `all` for every other package. `duplicated`: the owner started it from Duplicate on another package.",
    feature: "packages",
    sources: ["server"],
    props: z.object({
      product_id: id,
      session_count: count.nullable(),
      price_minor: amountMinor,
      currency,
      audience: packageAudience,
      package_kind: packageKind.optional(),
      /** A membership with no cap. */
      unlimited: z.boolean().optional(),
      /** Started from Duplicate on another package. */
      duplicated: z.boolean().optional(),
    }),
  }),
  sessions_adjusted: define({
    description:
      "The owner or an admin added or removed sessions on a participant (Add sessions, Remove sessions). `opening_balance`: the change touched a purchase of the hidden Opening balance package (made when the business had no package yet; migration 0057).",
    feature: "packages",
    sources: ["server"],
    props: z.object({ direction: z.enum(["add", "remove"]), session_count: count, opening_balance: z.boolean() }),
  }),
  package_frozen: define({
    description:
      "The owner or an admin paused packages of a client or a family (Freeze, migration 0071; Freeze all, D17). Each package end date moved by `days`. `reason` is the reason the owner picked. `scope`: `pack` (Freeze on one pack), `all` (Freeze all: every pack of the account), `participant` (the Freeze of one child: the packs bought for that child). `pack_count`: the packs paused; `skipped_count`: the packs in the scope that could not take the pause.",
    feature: "packages",
    sources: ["server"],
    props: z.object({ days: z.number().int().min(1), reason: packageFreezeReasonSchema, scope: freezeEventScope, pack_count: count, skipped_count: count }),
  }),
  package_unfrozen: define({
    description:
      "The owner or an admin ended pauses of packages early (End the pause; End all pauses, D17). Each end date moved back by the days not used. `days_used` is the most days one ended pause used (0 when no pause had started). `scope` as on `package_frozen`; `pack_count`: the pauses ended.",
    feature: "packages",
    sources: ["server"],
    props: z.object({ days_used: count, scope: freezeEventScope, pack_count: count }),
  }),
  package_extended: define({
    description:
      "The owner or an admin moved the last day of one package to a later day (Extend, #ai-feedback card #1). `days_added`: the days between the old and the new last day. The packages that wait for it move with it.",
    feature: "packages",
    sources: ["server"],
    props: z.object({ days_added: z.number().int().min(1) }),
  }),
  sessions_carried_over: define({
    description:
      "The unused sessions of a package moved to the package that waits for it (Carry over, #ai-feedback card #1, migration 0086). `by`: `owner` (Carry over on one package; the old package ends today or on its last paid session) or `expiry` (the expiry job, with the business setting Carry over on). `session_count`: the sessions moved.",
    feature: "packages",
    sources: ["server"],
    props: z.object({ session_count: z.number().int().min(1), by: z.enum(["owner", "expiry"]) }),
  }),
  membership_cancelled: define({
    description:
      "A monthly membership was cancelled (M2, migration 0079). `at`: `period_end` (no new renewal request, it ends with the paid month) or `now` (the owner ended it at once). `by`: the member in the app, or the owner or an admin on Desk.",
    feature: "packages",
    sources: ["server"],
    props: z.object({ at: z.enum(["period_end", "now"]), by: z.enum(["member", "owner"]), unlimited: z.boolean() }),
  }),
  team_member_added: define({
    description: "The owner added a coach, a co-owner or a front desk person (`frontdesk`).",
    feature: "team",
    sources: ["server"],
    props: z.object({ team_role: teamRole, method: z.enum(["direct", "invite"]) }),
  }),
  team_invitation_sent: define({
    description:
      "The owner sent a personal invitation to a team member: on add, or a new one from the Team list (`is_resend`). `delivery` is what WhatsApp answered: `sent`, `failed`, `no_phone` or `not_configured` (the owner can copy the link).",
    feature: "team",
    sources: ["server"],
    props: z.object({
      team_role: teamRole,
      delivery: z.enum(["sent", "failed", "no_phone", "not_configured"]),
      is_resend: z.boolean(),
    }),
  }),
  team_invitation_revoked: define({
    description: "The owner revoked the invitation of a team member who had not joined. They have no access until a new one.",
    feature: "team",
    sources: ["server"],
    props: z.object({ team_role: teamRole }),
  }),
  team_invitation_accepted: define({
    description:
      "A team member signed in for the first time with the number of their invitation. `hours_to_accept`: the hours from the send to the sign-in, rounded down.",
    feature: "team",
    sources: ["server"],
    props: z.object({ team_role: teamRole, hours_to_accept: count }),
  }),
  families_linked: define({
    description:
      "The owner linked two families that were both on the list (Link a parent who is already here, #ai-feedback 2026-10-26). One family went into the other: its children, packages, bookings and payments moved, and its parent became a second adult. `children_moved`, `packages_moved`, `bookings_moved`: what moved. `adult_count`: the adults of the family now. `same_name_children`: a child of each family had the same name (the check screen warned). Question: how often do owners find the same family two times?",
    feature: "people",
    sources: ["server"],
    props: z.object({
      link_id: id,
      children_moved: count,
      packages_moved: count,
      bookings_moved: count,
      adult_count: count,
      same_name_children: z.boolean(),
    }),
  }),
  family_link_undone: define({
    description: "The owner undid a link of two families. `minutes_after`: the minutes from the link to the Undo, rounded down.",
    feature: "people",
    sources: ["server"],
    props: z.object({ link_id: id, minutes_after: count }),
  }),
  parents_imported: define({
    description: "The owner brought parents into Teamna from a list.",
    feature: "setup",
    sources: ["server"],
    props: z.object({ source: z.enum(["whatsapp_group", "paste", "csv"]), contact_count: count }),
  }),
  whatsapp_group_connected: define({
    description:
      "Teamna found and joined the business's WhatsApp group. `method`: the owner added the Teamna number (`add_number`) or pasted the group invite link (`invite_link`).",
    feature: "whatsapp_sync",
    sources: ["server"],
    props: z.object({ group_link_id: id, method: z.enum(["add_number", "invite_link"]) }),
  }),
  whatsapp_invite_link_checked: define({
    description:
      "The owner checked a WhatsApp group invite link before Teamna joins. `result`: Teamna can join (`ok`), the owner is not a group admin (`not_admin`), Teamna syncs the group already (`synced_already`), or the link does not work (`invalid`). Never the link.",
    feature: "whatsapp_sync",
    sources: ["server"],
    props: z.object({ group_link_id: id, result: z.enum(["ok", "not_admin", "synced_already", "invalid"]) }),
  }),
  invites_sent: define({
    description: "The owner sent invites to join the business.",
    feature: "invites",
    sources: ["server"],
    props: z.object({
      channel: z.enum(["whatsapp", "sms", "email", "link", "mixed"]),
      invite_count: count,
      is_nudge: z.boolean(),
    }),
  }),
  member_decided: define({
    description: "The owner approved or rejected a person who asked to join.",
    feature: "invites",
    sources: ["server"],
    props: z.object({
      decision: z.enum(["approved", "rejected"]),
      /** Where the person acted: the Teamna mobile app (it sends X-Teamna-App) or Desk on the web. */
      client: z.enum(["app", "web"]),
    }),
  }),

  // 5.3 Joining
  invite_opened: define({
    description: "A person opened an invite link.",
    feature: "invites",
    sources: ["public_web", "desk_web", "mobile"],
    props: z.object({
      invite_kind: z.enum(["member", "staff", "unknown"]),
      /** Public page only: the page showed the "Runs on Teamna" link. The click itself is a GA4 visit with UTM values. */
      runs_on_teamna: z.boolean().optional(),
    }),
  }),
  business_joined: define({
    description:
      "A person joined a business. `via`: `code` an invite link or a typed code; `sign_in` the sign-in made the membership, because the business added the family; from the join screen of a parent with no place yet, `added` (Open, a family the business added, signed in before the rule), `invited` (Join, the business invited the number) and `ask_again` (Ask to join again after the membership ended; always `pending`, the owner approves).",
    feature: "invites",
    sources: ["server"],
    props: z.object({
      membership_status: z.enum(["active", "pending"]),
      joined_as: z.enum(["parent", "coach", "coowner", "frontdesk", "owner"]),
      via: z.enum(["code", "sign_in", "added", "invited", "ask_again"]),
    }),
  }),
  participant_added: define({
    description: "A child or a member was added to a business.",
    feature: "people",
    sources: ["server"],
    props: z.object({
      participant_id: id,
      added_by_role: bookingActor,
      /** Where it was added: the Teamna mobile app (it sends X-Teamna-App) or Desk on the web. */
      client: z.enum(["app", "web"]),
    }),
  }),
  participant_removed: define({
    description:
      "The owner deleted a swimmer: Remove child (`child`, with Undo) or Not our swimmer for a name a coach added that waits for a parent (`waiting`). From the swimmers list, a parent's page or the Link page. Question: how often do owners clean up swimmers added two times?",
    feature: "people",
    sources: ["server"],
    props: z.object({
      kind: z.enum(["child", "waiting"]),
      /** The participant id (`child`) or the waiting name's id (`waiting`). */
      participant_id: id,
    }),
  }),
  participant_confirmed: define({
    description:
      "The owner confirmed a child a parent added (Confirm, or a group picked on New participants). Once per child. `placed`: the child has a group now. Question: do owners clear this job from the phone's to-do list?",
    feature: "people",
    sources: ["server"],
    props: z.object({
      participant_id: id,
      placed: z.boolean(),
      /** Where the owner acted: the Teamna mobile app (it sends X-Teamna-App) or Desk on the web. */
      client: z.enum(["app", "web"]),
    }),
  }),
  // Link the swimmers coaches added to parents (link-parents-sync, migration 0041)
  parent_matches_found: define({
    description:
      "The owner asked Teamna to find the parents of the waiting swimmers. `source`: the language model (`ai`) or the family-name rule (`rule`). `fell_back`: the model failed and the rule ran.",
    feature: "link_parents",
    sources: ["server"],
    props: z.object({
      waiting_count: count,
      match_count: count,
      likely_count: count,
      maybe_count: count,
      sibling_count: count,
      source: z.enum(["ai", "rule"]),
      fell_back: z.boolean(),
    }),
  }),
  group_names_cleaned: define({
    description:
      "The AI read the WhatsApp names of the parents' group that it did not read yet (step 1 of New swimmers). `cleaned_count`: names it wrote in a plainer form. `fell_back`: the model failed and the WhatsApp names stay.",
    feature: "link_parents",
    sources: ["server"],
    props: z.object({ asked_count: count, cleaned_count: count, fell_back: z.boolean() }),
  }),
  parents_asked: define({
    description: "The owner copied the message for the parents' WhatsApp group (`post`) or the reminder (`reminder`), on New swimmers step 4.",
    feature: "link_parents",
    sources: ["server"],
    props: z.object({ kind: z.enum(["post", "reminder"]) }),
  }),
  parent_claim_sent: define({
    description:
      "A parent of the group answered on the find page: these waiting names are my children. `relation`: what they said they are. `note_added`: they wrote a note to the business (never the text).",
    feature: "link_parents",
    sources: ["server"],
    props: z.object({ child_count: count, relation: z.enum(["mother", "father", "other"]), note_added: z.boolean() }),
  }),
  parent_claim_decided: define({
    description:
      "The owner checked an answer of a parent or a coach. `decision`: `linked` or `rejected`. `second_adult`: a child of the answer had a parent already (for example the mother and the father both answered), and the person became a second adult of that family; `linked_count` counts the waiting names that joined it. `wait_hours`: full hours from the answer to the decision (the To do list on Home should make it shorter).",
    feature: "link_parents",
    sources: ["server"],
    props: z.object({
      claim_id: id,
      decision: z.enum(["linked", "rejected"]),
      source: z.enum(["parent", "coach"]),
      linked_count: count,
      second_adult: z.boolean(),
      wait_hours: count,
      /** Where the person acted: the Teamna mobile app (it sends X-Teamna-App) or Desk on the web. */
      client: z.enum(["app", "web"]),
    }),
  }),
  parent_ask_sent: define({
    description: "The owner asked a coach on WhatsApp who the parent of a waiting name is (New swimmers, Still missing). `whatsapp_status`: what WhatsApp said.",
    feature: "link_parents",
    sources: ["server"],
    props: z.object({ child_count: count, whatsapp_status: z.enum(["sent", "failed", "no_phone", "not_configured"]) }),
  }),
  parent_ask_answered: define({
    description: "A coach answered \"Parent wanted\": picked a person of the group (`pick`), typed a name and number (`type`), or does not know (`dont_know`).",
    feature: "link_parents",
    sources: ["server"],
    props: z.object({ answer: z.enum(["pick", "type", "dont_know"]) }),
  }),
  parent_match_dismissed: define({
    description: "The owner said a parent match from Teamna is not right.",
    feature: "link_parents",
    sources: ["server"],
    props: z.object({ match_id: id, confidence: z.enum(["likely", "maybe", "siblings"]) }),
  }),
  pending_participant_linked: define({
    description:
      "The owner linked a swimmer a coach added to a parent. `link_kind`: how (`contact` is a person of the synced WhatsApp group). `via`: the owner found the parent (`owner`), took a match from Teamna (`match`), or linked the answer of a parent (`parent`) or a coach (`coach`).",
    feature: "link_parents",
    sources: ["server"],
    props: z.object({
      pending_participant_id: id,
      participant_id: id,
      link_kind: z.enum(["family", "new_family", "self", "participant", "contact"]),
      via: z.enum(["owner", "match", "parent", "coach"]),
      /** False when the parent has no name yet (the owner linked a number only). Sent on the owner's own link. */
      parent_named: z.boolean().optional(),
      /** Where the person acted: the Teamna mobile app (it sends X-Teamna-App) or Desk on the web. */
      client: z.enum(["app", "web"]),
    }),
  }),
  pending_participant_unlinked: define({
    description: "The owner took back a link, and the swimmer waits for a parent again.",
    feature: "link_parents",
    sources: ["server"],
    props: z.object({ pending_participant_id: id, link_kind: z.enum(["family", "new_family", "self", "participant", "contact"]) }),
  }),
  group_person_named: define({
    description: "The owner typed a name for a person of a synced WhatsApp group or of a pasted list.",
    feature: "link_parents",
    sources: ["server"],
    props: z.object({ contact_id: id }),
  }),
  contacts_file_read: define({
    description:
      "The owner chose a phone contacts file (.vcf) on New swimmers, step \"Who are these people?\", and the browser read it. Sent also when no number of the group matched. Nothing is saved yet. `contact_count` is the contact cards with a name and a number in the file, `matched_count` the people with no name that the file has a name for. `relation_count` the people the file has as a relation of a child (\"Kareem Mom\"): Teamna does not use those as names.",
    feature: "link_parents",
    sources: ["desk_web"],
    props: z.object({ contact_count: count, matched_count: count, relation_count: count.optional() }),
  }),
  group_people_named_from_file: define({
    description:
      "The owner saved the names from a phone contacts file for the people of a synced WhatsApp group. `sent_count` is the names sent, `named_count` the people who took a name (a person with a name already keeps it).",
    feature: "link_parents",
    sources: ["server"],
    props: z.object({ sent_count: count, named_count: count }),
  }),

  // 5.4 Bookings, sessions, attendance
  package_reminder_sent: define({
    description:
      "Tell the client (#ai-feedback 2026-10-26): the business could not add a client to a session because no package pays for it, and sent the client a message in their private thread instead. `reason`: the refusal the owner's panel showed when they chose to send (from the refused add just before; not checked again) (no_package for this class, no_sessions, package_unpaid: sessions left on a package that is not paid yet, package_ends, package_paused, membership_cap, month_unpaid). `pay_link`: the message has the link to buy a package. Never the text.",
    feature: "bookings",
    sources: ["server"],
    props: z.object({
      session_id: id,
      reason: z.enum(["no_package", "no_sessions", "package_unpaid", "package_ends", "package_paused", "membership_cap", "month_unpaid"]),
      pay_link: z.boolean(),
    }),
  }),
  booking_created: define({
    description:
      "A place in a session was booked. `batch_size` is the number of dates booked with one Confirm (the month booking), 1 for a single booking. `client`: app (the request sent X-Teamna-App) or web. `session_kind`: a group class or a private session. `held`: the member still held the place when they booked it (the 10-minute hold). `open_to_all`: the session's class is open to all groups. `days_ahead`: whole days from the day of the booking to the day of the session, in the business timezone (0: the same day). `entry` (Studio Home H2, optional): where the member started, `open_spots` (the link in the owner's open spots message), `book_tab` (the Book tab), `book_again`, `month`, `waitlist`; missing from older apps and from bookings an owner makes. `owner_override` (Add anyway, #ai-feedback 2026-10-26): the business booked it with no package, `owed` or `free`.",
    feature: "bookings",
    sources: ["server"],
    props: z.object({
      booking_id: id,
      session_id: id,
      group_id: id.nullable(),
      open_to_all: z.boolean().optional(),
      booked_by_role: bookingActor,
      /** Add anyway (#ai-feedback 2026-10-26): the business booked it with no package, owed or free. Missing for an ordinary booking. */
      owner_override: z.enum(["owed", "free"]).optional(),
      batch_size: count,
      client: z.enum(["app", "web"]),
      session_kind: z.enum(["group", "private"]),
      held: z.boolean(),
      days_ahead: count.optional(),
      entry: bookingEntrySchema.optional(),
    }),
  }),
  every_week_answered: define({
    description:
      "The Book tab: after a pick, the card offered the same session every week until the package ends. `answer`: every_week (the parent took it) or just_this_one. `dates`: dates picked by the answer, `waitlisted`: full dates whose waiting list it joined.",
    feature: "bookings",
    sources: ["desk_web", "mobile"],
    props: z.object({
      answer: z.enum(["every_week", "just_this_one"]),
      dates: count,
      waitlisted: count,
    }),
  }),
  waitlist_joined: define({
    description:
      "A parent joined the waiting lists of full sessions (the Book tab). `count`: the sessions joined with one tap, more than 1 from the \"every week\" offer.",
    feature: "bookings",
    sources: ["server"],
    props: z.object({ count, client: z.enum(["app", "web"]) }),
  }),
  waitlist_left: define({
    description: "A parent left a waiting list.",
    feature: "bookings",
    sources: ["server"],
    props: z.object({ client: z.enum(["app", "web"]) }),
  }),
  waitlist_promoted: define({
    description:
      "A place that opened went to the first person on a waiting list (`outcome` booked, one session spent), or the booking was refused and the person left the list (`outcome` missed, `reason` the error code). `mode` (B2): `auto`, the worker booked an early place; `offer`, the person accepted a late place that was offered (sent by the accept), or the check before an offer refused it (`missed`).",
    feature: "bookings",
    sources: ["server"],
    props: z.object({
      session_id: id,
      outcome: z.enum(["booked", "missed"]),
      reason: z.string().nullable(),
      hours_before_start: z.number(),
      mode: z.enum(["auto", "offer"]),
    }),
  }),
  waitlist_offer_sent: define({
    description:
      "Waitlist offers (B2): a late place (fewer than the business's late window before the start) went to the first person in line as an offer, by push, and by WhatsApp when the person said yes to WhatsApp: the Meta template once Meta approves `waitlist_offer`, a Whapi text until then (D48). `hours_before_start`: from the offer to the start, one decimal.",
    feature: "bookings",
    sources: ["server"],
    props: z.object({
      session_id: id,
      hours_before_start: z.number(),
    }),
  }),
  waitlist_offer_answered: define({
    description:
      "Waitlist offers (B2): the end of an offer. `result`: `accepted` (booked through the normal booking), `declined` (the person said no; the place goes to the next person) or `expired` (the time to accept ended; sent by the worker). `minutes_to_answer`: from the offer to the answer, whole minutes.",
    feature: "bookings",
    sources: ["server"],
    props: z.object({
      session_id: id,
      result: z.enum(["accepted", "declined", "expired"]),
      minutes_to_answer: count,
    }),
  }),
  session_hold_lost: define({
    description:
      "The Book tab: a parent picked session places, and the server could not hold some of them (for example another family booked or held the place first). Sent only when a place is lost.",
    feature: "bookings",
    sources: ["server"],
    props: z.object({
      lost_count: count,
      held_count: count,
      client: z.enum(["app", "web"]),
    }),
  }),
  booking_cancelled: define({
    description: "A booking was cancelled.",
    feature: "bookings",
    sources: ["server"],
    props: z.object({
      booking_id: id,
      cancelled_by_role: bookingActor,
      hours_before_start: z.number(),
      session_returned: z.boolean(),
    }),
  }),
  booking_rescheduled: define({
    description: "A parent moved a booking to another session.",
    feature: "bookings",
    sources: ["server"],
    props: z.object({ booking_id: id, hours_before_start: z.number() }),
  }),
  attendance_marked: define({
    description:
      "Attendance was saved for a session. `method` (I1, 2026-10-24): `checkin` is one tap for each client who comes, saved at once (businesses where clients pick each class; also the job that marks the rest absent 10 minutes after the start, with role `system` and no `client`), `headcount` the cards, `list` the list. An older app that does not say sends the person's chosen way (cards is `headcount`). `minutes_from_start` is negative before the start.",
    feature: "attendance",
    sources: ["server"],
    props: z.object({
      session_id: id,
      marked_count: count,
      attended_count: count,
      absent_count: count,
      minutes_from_start: z.number().int(),
      method: attendanceMethodSchema,
      /** Where the person saved: the Teamna mobile app (it sends X-Teamna-App) or Desk on the web. Absent for the job. */
      client: z.enum(["app", "web"]).optional(),
      /** Bookings that took no session and took one now, because Came was saved on them (2026-10-30). */
      sessions_taken_count: count.optional(),
    }),
  }),
  first_class_welcome_sent: define({
    description:
      "The worker queued the welcome before a person's first class at the business (C3), by push, and by WhatsApp with a WhatsApp yes: the Meta template once Meta approves `first_class_welcome`, a Whapi text until then (D48). Once for each participant and business. `hours_before`: the hours from the send to the class start (the owner picks the most, a late booking gets it later). Sent for the member's account with role `system`.",
    feature: "attendance",
    sources: ["server"],
    props: z.object({ hours_before: z.number().int().min(0) }),
  }),
  attendance_opened: define({
    description:
      "An owner or an admin pressed Take attendance on Desk after opening a session from a Today list (2026-10-19). `source`: `home_today` when the session was opened with Open session on the Home card (the session panel over Home since 2026-10-22; before, the Calendar's Day view on a computer and the day list's sheet on a phone), `calendar_today` when it was a click on the Day view of the Calendar (named Today before 2026-10-21) while it shows today, `calendar_day` when the Day view shows another day (2026-10-21). The save itself is `attendance_marked`.",
    feature: "attendance",
    sources: ["desk_web"],
    props: z.object({ source: z.enum(["home_today", "calendar_today", "calendar_day"]) }),
  }),
  session_extra_added: define({
    description:
      "A coach or an owner added a swimmer who came and was not booked (Someone else came). `kind`: a swimmer of the club (`participant`), a name that waits for a parent (`waiting`), or a new name (`new`). `from`: picked from the level's Did not book list (`not_booked`, 2026-10-19), found with the search (`search`), typed as a new name (`new`), or answered Came on the attendance register, which lists the level's swimmers who did not book (`register`, 2026-10-22). An older app that does not say is `search`. `session` (2026-10-29, Came takes 1 session at once): `taken` from a package, `owed` when no package had a session left (a later package or paid month that covers this class (its class and kind) pays it), `none` when nothing is taken (no package for the class ever, a name that waits for a parent, or a booking that was there already).",
    feature: "attendance",
    sources: ["server"],
    props: z.object({
      session_id: id,
      kind: z.enum(["participant", "waiting", "new"]),
      from: z.enum(["not_booked", "search", "new", "register"]),
      /** Where the person added: the Teamna mobile app (it sends X-Teamna-App) or Desk on the web. */
      client: z.enum(["app", "web"]),
      session: z.enum(["taken", "owed", "none"]).optional(),
    }),
  }),
  came_during_pause_answered: define({
    description:
      "The owner or an admin answered Came during the pause in To do (2026-10-29): Came took a session from a paused package, and the pause stayed. `answer`: `end` ended the pause today (the family hears it, as with End the pause), `keep` kept it. `days_used`: the paused days used, with `end`.",
    feature: "attendance",
    sources: ["server"],
    props: z.object({ answer: z.enum(["end", "keep"]), days_used: count }),
  }),
  session_not_booked_opened: define({
    description:
      "An owner or an admin opened the Did not book tab of a session on Desk (the Calendar's session panel or the phone sheet, 2026-10-19): the swimmers of the level with no booking for it.",
    feature: "attendance",
    sources: ["desk_web"],
    props: z.object({}),
  }),
  session_extra_decided: define({
    description:
      "The owner decided a visit: a swimmer who came and was not booked. `money`: 1 session taken from the family's package (`take`) or let go (`free`). `add_weekday`: the session's day was added to the swimmer's days.",
    feature: "attendance",
    sources: ["server"],
    props: z.object({
      money: z.enum(["take", "free"]),
      add_weekday: z.boolean(),
      /** Where the owner decided: the Teamna mobile app (it sends X-Teamna-App) or Desk on the web. */
      client: z.enum(["app", "web"]),
    }),
  }),
  attendance_queued: define({
    description:
      "The phone had no signal when a coach or owner saved attendance, so the app kept the save on the phone to send later (mobile V2 slice 1.2).",
    feature: "attendance",
    sources: ["mobile"],
    props: z.object({ session_id: id, marked_count: count }),
  }),
  attendance_queue_sent: define({
    description:
      "A save the phone kept with no signal reached Teamna, or was refused. `outcome`: saved; changed (another device changed the session first, the person takes it again); refused (the API said no, for example the person left the business).",
    feature: "attendance",
    sources: ["mobile"],
    props: z.object({ session_id: id, waited_minutes: count, outcome: z.enum(["saved", "changed", "refused"]) }),
  }),
  session_cancelled: define({
    description: "The business cancelled a session.",
    feature: "schedule",
    sources: ["server"],
    props: z.object({ session_id: id, booking_count: count }),
  }),
  running_late_sent: define({
    description: "A coach or the owner told the members that a session starts late.",
    feature: "schedule",
    sources: ["server"],
    props: z.object({ session_id: id, minutes_late: count }),
  }),
  // Cover requests between staff (Pilates studio shape S3, migration 0072). Question: how fast do
  // covers get taken? Report: time to cover (`minutes_to_answer` of `taken` and `approved`), covers each week.
  cover_requested: define({
    description:
      "A staff member asked for cover for one session they run. The other staff of that class (else the whole team) got the offer. `hours_before_start`: how long before the session starts.",
    feature: "schedule",
    sources: ["server"],
    props: z.object({ hours_before_start: z.number() }),
  }),
  cover_answered: define({
    description:
      "A cover request got an answer. `result`: `taken` (the first yes took the session), `awaiting_approval` (the first yes, with Approve each cover on; the session moves when the owner approves), `declined` (one person said Not me; the request stays open), `approved` or `refused` (the owner decided a yes, with Approve each cover on), `cancelled` (taken back by the person who asked or the owner, or the session was cancelled, moved or got another staff member), `expired` (nobody took it before the session started; sent by the worker). `minutes_to_answer`: from the request to this answer.",
    feature: "schedule",
    sources: ["server"],
    props: z.object({
      result: z.enum(["taken", "awaiting_approval", "declined", "expired", "cancelled", "approved", "refused"]),
      minutes_to_answer: count,
    }),
  }),

  // 5.5 Payments
  checkout_started: define({
    description: "A card checkout was opened.",
    feature: "payments",
    sources: ["server"],
    props: z.object({ kind: checkoutKind, amount_minor: amountMinor, currency, package_kind: packageKind.optional() }),
  }),
  payment_succeeded: define({
    description:
      "A card payment succeeded (from the payment provider webhook). `package_audience` is set when the payment is for a package (`package`, `owed_balance`): `new_clients` for an intro offer, `all` for every other package. `package_kind` (`pack`, `membership`, `intro`) is set for a package and for a membership renewal (a `recurring_fee` request of a membership).",
    feature: "payments",
    sources: ["server"],
    props: z.object({
      payment_id: id,
      kind: paymentKind,
      amount_minor: amountMinor,
      currency,
      provider: z.string(),
      package_audience: packageAudience.optional(),
      package_kind: packageKind.optional(),
    }),
  }),
  payment_failed: define({
    description: "A card payment failed (from the payment provider webhook).",
    feature: "payments",
    sources: ["server"],
    props: z.object({ payment_id: id, kind: paymentKind.or(z.literal("unknown")), provider: z.string() }),
  }),
  payment_recorded: define({
    description:
      "The business recorded a payment that did not go through Teamna (cash, bank transfer). `package_audience`: `new_clients` when the package is an intro offer, `all` for every other package. `package_kind`: `pack`, `membership` or `intro`. `membership_renewal` is a month of a membership paid at the desk. `package_start` (a pack sold at the desk, card #1): `after_package` when it waits for the package before it, `today` when it starts on the day of the sale. `with_proof`: a proof of payment (a photo or PDF of the receipt or the transfer) went on the payment when it was recorded (migration 0084).",
    feature: "payments",
    sources: ["server"],
    props: z.object({
      kind: z.enum(["package_at_desk", "owed_balance", "membership_renewal"]),
      method: z.string(),
      amount_minor: amountMinor,
      currency,
      package_audience: packageAudience.optional(),
      package_kind: packageKind.optional(),
      package_start: z.enum(["today", "after_package"]).optional(),
      with_proof: z.boolean().optional(),
    }),
  }),
  payment_proof_changed: define({
    description:
      "The owner or an admin changed the proof of a payment recorded before, from the Payments page (migration 0084). `action`: `attached` (it had none), `replaced` or `removed`. `file_type`: `image` or `pdf`, the new file (the old one for `removed`). A proof sent with the payment is `payment_recorded` with `with_proof`.",
    feature: "payments",
    sources: ["server"],
    props: z.object({ action: z.enum(["attached", "replaced", "removed"]), file_type: z.enum(["image", "pdf"]) }),
  }),
  payment_request_created: define({
    description:
      "The business asked a family for an amount. `membership`: the renewal request of a monthly membership (M2), raised by the job before the renewal day.",
    feature: "payments",
    sources: ["server"],
    props: z.object({ kind: z.enum(["one_off", "recurring_fee", "membership"]), amount_minor: amountMinor, currency }),
  }),
  fee_request_decided: define({
    description:
      "The owner or an admin decided a no-show or late-cancel fee from the To do list (I2). `kind`: `no_show` (booked and marked absent) or `late_cancel` (the member cancelled after the free-cancel time). `decision`: `sent` (a one-off payment request with a card link; `payment_request_created` comes with it) or `waived`. Sent once for each fee, never on a double tap.",
    feature: "payments",
    sources: ["server"],
    props: z.object({
      kind: z.enum(["no_show", "late_cancel"]),
      decision: z.enum(["sent", "waived"]),
      amount_minor: amountMinor,
      currency,
    }),
  }),
  pay_link_opened: define({
    description: "A person opened a payment link page.",
    feature: "payments",
    sources: ["public_web"],
    props: z.object({
      state: z.enum(["open", "paid", "closed", "not_found"]),
      /** The page showed the "Runs on Teamna" link. The click itself is a GA4 visit with UTM values. */
      runs_on_teamna: z.boolean().optional(),
    }),
  }),
  payout_onboarding_started: define({
    description: "The owner started the Stripe Connect onboarding to receive card payments.",
    feature: "payouts",
    sources: ["server"],
    props: none,
  }),
  payout_account_status_changed: define({
    description: "The status of the business's payout account changed.",
    feature: "payouts",
    sources: ["server"],
    props: z.object({ status: z.string() }),
  }),
  subscription_status_changed: define({
    description: "The status of the business's Teamna subscription changed.",
    feature: "billing",
    sources: ["server"],
    props: z.object({ status: z.string(), billing_event: z.string() }),
  }),

  // 5.6 Messages and notifications
  message_sent: define({
    description: "A person sent a chat message. Never the text.",
    feature: "messages",
    sources: ["server"],
    props: z.object({ conversation_kind: z.string(), sender_role: bookingActor }),
  }),
  // Support tickets (support-system.md, section 11). Control Center is not tracked, so team replies and moves send nothing.
  support_ticket_created: define({
    description: "A person at a business (owner, admin or staff) sent a support request to Teamna. Never the subject or the text.",
    feature: "support",
    sources: ["server"],
    props: z.object({
      ticket_id: id,
      urgency: z.enum(["low", "normal", "blocking"]),
      platform: z.enum(["web", "ios", "android", "unknown"]),
      file_count: count,
    }),
  }),
  support_reply_sent: define({
    description: "A person at a business replied on their support request. Never the text.",
    feature: "support",
    sources: ["server"],
    props: z.object({
      ticket_id: id,
      file_count: count,
      from_kind: z.enum(["open", "fix_pending", "waiting_customer", "closed"]),
      reopened: z.boolean(),
    }),
  }),
  support_ticket_rated: define({
    description: "A person at a business said if Teamna's answer solved their resolved support request.",
    feature: "support",
    sources: ["server"],
    props: z.object({ ticket_id: id, rating: z.enum(["up", "down"]) }),
  }),
  announcement_sent: define({
    description: "The owner sent an announcement to members.",
    feature: "messages",
    sources: ["server"],
    props: z.object({ recipient_count: count }),
  }),
  notification_sent: define({
    description:
      "Teamna sent a notification to a person (or the send failed). `whatsapp_via` (WhatsApp rows only): `template` is the approved Meta template, `text` is a plain text from the Teamna number through Whapi while the template waits for approval (D48).",
    feature: "notifications",
    sources: ["server"],
    props: z.object({
      notification_type: z.string(),
      channel: z.string(),
      status: z.enum(["sent", "failed"]),
      whatsapp_via: z.enum(["template", "text"]).optional(),
    }),
  }),

  // AI Assistant (strategy repo teamna/strategy/ai-assistant-v1.md, section 9)
  assistant_turn_answered: define({
    description: "AI Assistant answered a question of the owner or an admin (also a failed answer, with its outcome).",
    feature: "ai_assistant",
    sources: ["server"],
    props: z.object({
      input_mode: z.enum(["text", "voice"]),
      outcome: z.enum(assistantOutcomes),
      /** Outcome `no_tool`: the shape of what no tool answers. Never the question. Null for other outcomes. */
      gap_kind: z.enum(assistantGapKinds).nullable(),
      tool_count: count,
      /** The names of the tools of the turn, in call order. Never their input or result. */
      tools: z.array(z.string()),
      /** The datasets of the query calls that worked (generic data), in call order. Never the rest of the spec. */
      datasets: z.array(z.string()),
      /** The chart kinds of the answer, in screen order. Empty: tables only. */
      chart_kinds: z.array(z.enum(assistantChartKinds)),
      /** Query calls that failed (a spec that does not fit the dataset, a wrong name). */
      query_errors: count,
      has_proposal: z.boolean(),
      latency_ms: count,
      input_tokens: count,
      output_tokens: count,
      cost_micros: count,
      turn_index: count,
      /** What the prompt is about (a small model reads it; analytics never gets the prompt). Null: the label failed. */
      topic: z.enum(assistantTopics).nullable(),
      /** How the business has AI Assistant: it bought the package, or Teamna gave it. */
      access_source: z.enum(["package", "grant"]),
      client: z.enum(["app", "web"]),
    }),
  }),
  assistant_turn_rated: define({
    description: "The owner or an admin gave an answer of AI Assistant a thumbs up or down, or took the rating back.",
    feature: "ai_assistant",
    sources: ["server"],
    props: z.object({
      rating: z.enum(["up", "down", "cleared"]),
      /** The reason of a thumbs down, an enum. Null for up, for cleared, and for a down with no reason. */
      reason: z.enum(assistantRatingReasons).nullable(),
      outcome: z.enum(assistantOutcomes).nullable(),
      topic: z.enum(assistantTopics).nullable(),
      turn_index: count,
      client: z.enum(["app", "web"]),
    }),
  }),
  assistant_voice_transcribed: define({
    description:
      "The owner or an admin recorded a question to AI Assistant, and Teamna turned it into text for the compose box (or failed). The question is counted when it is sent, in assistant_turn_answered with input_mode voice.",
    feature: "ai_assistant",
    sources: ["server"],
    props: z.object({
      /** `ok`: text came back. `empty`: the recording had no words. `failed`: the transcription service failed. */
      outcome: z.enum(["ok", "empty", "failed"]),
      /** The length of the recording, whole seconds, as the browser measured it. */
      audio_seconds: count,
      client: z.enum(["app", "web"]),
    }),
  }),
  assistant_package_changed: define({
    description: "The owner or an admin added the AI Assistant package in Desk, removed it, or kept it after a remove.",
    feature: "ai_assistant",
    sources: ["server"],
    props: z.object({
      change: z.enum(["added", "removed", "kept"]),
      /** The subscription was in its free trial at the change. */
      in_trial: z.boolean(),
    }),
  }),
  assistant_offer_viewed: define({
    description: "The owner or an admin saw the locked AI Assistant screen with the offer (the package is not on).",
    feature: "ai_assistant",
    sources: ["desk_web", "mobile"],
    props: z.object({
      /** The locked AI Assistant page, or the AI Assistant part of the Billing page. */
      screen: z.enum(["assistant_page", "billing"]),
      /** The owner can add it here now (false: billing is off, a demo, read-only, or the app). */
      can_add: z.boolean(),
    }),
  }),
  assistant_card_created: define({
    description: "The morning detectors of AI Assistant found a suggestion for a business.",
    feature: "ai_assistant",
    sources: ["server"],
    props: z.object({ kind: z.enum(assistantCardKinds) }),
  }),
  assistant_card_opened: define({
    description: "The owner or an admin opened a suggestion of AI Assistant (first open makes the chat).",
    feature: "ai_assistant",
    sources: ["server"],
    props: z.object({
      kind: z.enum(assistantCardKinds),
      /** Where it was opened: the side sheet on Home, or the AI Assistant page. */
      entry: z.enum(["home_sheet", "assistant_page"]),
      first_open: z.boolean(),
      client: z.enum(["app", "web"]),
    }),
  }),
  assistant_more_opened: define({
    description: "The owner or an admin tapped More under a table of AI Assistant: the same tool ran again one level deeper, with no model call.",
    feature: "ai_assistant",
    sources: ["server"],
    props: z.object({
      /** The name of the read tool that ran, as in `tools` of assistant_turn_answered. Never its input. */
      tool: z.string(),
      client: z.enum(["app", "web"]),
    }),
  }),
  assistant_action_decided: define({
    description: "The owner or an admin tapped Confirm on a plan of AI Assistant, with what happened.",
    feature: "ai_assistant",
    sources: ["server"],
    props: z.object({
      /** `stale`: the data changed after the plan, nothing happened. `failed`: the change failed. */
      decision: z.enum(["confirmed", "stale", "failed"]),
      /** The kind of the suggestion, or `question` for a chat the person started. */
      kind: z.enum([...assistantCardKinds, "question"]),
      tool: z.enum(assistantActionTools),
      /** Prompts of the person before the Confirm. 0: confirmed as AI Assistant made it. */
      turns_before: count,
      /** The version of the plan: 1 is the first plan of the chat. */
      version: count,
      option_index: count.nullable(),
      client: z.enum(["app", "web"]),
    }),
  }),
  assistant_card_closed: define({
    description: "The owner or an admin ended a chat without a Confirm: Mark done, Not now, or Close with a reason.",
    feature: "ai_assistant",
    sources: ["server"],
    props: z.object({
      kind: z.enum([...assistantCardKinds, "question"]),
      how: z.enum(["marked_done", "not_now", "closed"]),
      /** The reason of a Close, an enum. Null for Mark done and Not now. */
      reason: z.enum(assistantCloseReasons).nullable(),
      stopped_plans: count,
      client: z.enum(["app", "web"]),
    }),
  }),

  // Name search (one rule on every search box, strategy repo artifact "Smart name search")
  name_search_used: define({
    description:
      "A person typed a name in a search box and stopped typing. Once for each typed text. `top_band` says how the best row matched: exact, sounds (another spelling), typo (a close spelling), arabic, or none (nothing found).",
    feature: "search",
    sources: ["desk_web", "mobile"],
    props: z.object({
      search_box: z.enum(nameSearchBoxes),
      word_count: count,
      result_count: count,
      top_band: z.enum([...nameBands, "none"]),
    }),
  }),

  // Reports (Pilates studio shape, T2: classes per staff member, for pay)
  report_viewed: define({
    description:
      "The owner or an admin opened a report (this month) or moved it to another month. `months_back` is 0 for this month, 1 for last month. `scope`: `team` is the report page of the whole team; `person` is the month block on the page of one staff member (Staff, then a person).",
    feature: "reports",
    sources: ["desk_web"],
    props: z.object({ report: z.enum(["staff_classes"]), scope: z.enum(["team", "person"]), months_back: count }),
  }),
  report_exported: define({
    description: "The owner or an admin downloaded a report as a CSV file. `row_count` is the number of rows under the header.",
    feature: "reports",
    sources: ["server"],
    props: z.object({ report: z.enum(["staff_classes"]), row_count: count, months_back: count }),
  }),

  // Health notes and the terms at join (Pilates studio shape, C2, migration 0077). Never a question,
  // an answer or a note: health data stays out of analytics.
  health_form_completed: define({
    description:
      "A member answered the health questions of a business for one person (themselves, or a child in a family business), when they joined or from the reminder. `has_flag`: a yes on any question, so the team sees the Health note flag.",
    feature: "clients",
    sources: ["server"],
    props: z.object({ has_flag: z.boolean(), client: z.enum(["app", "web"]) }),
  }),
  waiver_accepted: define({
    description: "A member accepted the terms (waiver) of a business for one person, with the health questions. `waiver_version` is the version of the text they accepted.",
    feature: "clients",
    sources: ["server"],
    props: z.object({ waiver_version: count, client: z.enum(["app", "web"]) }),
  }),
  health_form_changed: define({
    description:
      "The owner or an admin saved the health form in Settings, Health: on or off, the questions, who reads the answers, or the terms. `waiver_changed`: the save made a new version of the terms. Never the text.",
    feature: "clients",
    sources: ["server"],
    props: z.object({
      enabled: z.boolean(),
      staff_can_read: z.boolean(),
      question_count: count,
      waiver_version: count.nullable(),
      waiver_changed: z.boolean(),
    }),
  }),

  // Clients table (Pilates studio shape, C1)
  client_list_filtered: define({
    description:
      "The owner or an admin picked a filter on the Clients table (where clients book for themselves): all, active, ending soon, intro offer or quiet.",
    feature: "clients",
    sources: ["desk_web", "mobile"],
    props: z.object({ filter: clientFilterSchema }),
  }),
  clients_exported: define({
    description: "The owner or an admin downloaded the Clients table as a CSV file. `row_count` is the number of rows under the header. `filter` is the filter of the table.",
    feature: "clients",
    sources: ["server"],
    props: z.object({ row_count: count, filter: clientFilterSchema }),
  }),
  clients_messaged: define({
    description:
      "The owner or an admin sent one message to many clients from the Clients table, into each private thread. `scope` is `selected` (ticked rows) or `filter` (every client of the filter). The counts say how many got it, how many are not in the app yet, and how many the hourly limit stopped. Never the text.",
    feature: "clients",
    sources: ["server"],
    props: z.object({
      scope: z.enum(["selected", "filter"]),
      filter: clientFilterSchema.optional(),
      recipient_count: count,
      sent_count: count,
      not_joined_count: count,
      limited_count: count,
    }),
  }),

  // Studio Home (H1 to H4)
  home_card_used: define({
    description:
      "The owner or an admin used a card on owner Home. `card`: today (the sessions of today with their fill), open_spots (free places in the next 48 hours), renewals (clients to renew, where clients book for themselves) or tile (a number tile, named in `tile`: participants, came, booked, quiet and run for a family business; fill, active, ending, intro and late for a studio). `action`: open_session (a session opened from the card), open_calendar, open_panel (a tile or a list opened), send_one or send_all (a message from the card; the send itself is the server event `home_nudge_sent`). A copy or a share of the open spots message is `open_spots_shared`.",
    feature: "owner_home",
    sources: ["desk_web", "mobile"],
    props: z.object({
      card: z.enum(["today", "open_spots", "renewals", "tile"]),
      action: z.enum(["open_session", "open_calendar", "open_panel", "send_one", "send_all"]),
      tile: z.enum(["participants", "came", "booked", "quiet", "run", "fill", "active", "ending", "intro", "late"]).optional(),
    }),
  }),
  open_spots_shared: define({
    description:
      "The owner or an admin took the open spots message from Home (where clients book for themselves): the free places of the next 48 hours, each time with a Book link. `channel`: copy (Desk, to the clipboard) or share_sheet (the phone's share sheet). `spots_count` is the free places, `sessions_count` the sessions in the message. `from`: home (the Home card; left out by older clients) or assistant (the same message in a suggestion chat of AI Assistant, Studio A1). Never the text.",
    feature: "owner_home",
    sources: ["desk_web", "mobile"],
    props: z.object({
      spots_count: count,
      sessions_count: count,
      channel: z.enum(["copy", "share_sheet"]),
      from: z.enum(["home", "assistant"]).optional(),
    }),
  }),
  home_nudge_sent: define({
    description:
      "Messages from owner Home went into members' private threads. One event for each kind and reason in the request. `kind`: quiet (we miss you), pay_reminder or renewal. `reason` (Renewals list, where clients book for themselves): low_sessions, ends_soon or intro_not_converted. `recipients_count` counts the messages this request sent: a repeat of a message sent a moment ago, a member not in the app and a row that changed do not count. Never the text or a name.",
    feature: "owner_home",
    sources: ["server"],
    props: z.object({
      kind: ownerNudgeKindSchema,
      reason: renewalReasonSchema.optional(),
      recipients_count: count,
    }),
  }),

  // 5.7 Activation
  owner_activated: define({
    description: "The business has its setup done and its first session with an attended member.",
    feature: "activation",
    sources: ["server"],
    props: z.object({ days_since_business_created: count }),
  }),
  coach_activated: define({
    description: "A coach or co-owner marked attendance for the first time in this business.",
    feature: "activation",
    sources: ["server"],
    props: z.object({ days_since_joined: count.nullable() }),
  }),
  parent_activated: define({
    description: "A parent made a first booking or a first payment in this business.",
    feature: "activation",
    sources: ["server"],
    props: z.object({ days_since_joined: count.nullable(), first_action: z.enum(["booking", "payment"]) }),
  }),
} as const;

export type AnalyticsEvents = typeof analyticsEvents;
export type AnalyticsEventName = keyof AnalyticsEvents;
export type AnalyticsProps<E extends AnalyticsEventName> = z.infer<AnalyticsEvents[E]["props"]>;

/** Events a given source may send. A client cannot send a server event by mistake. */
export type AnalyticsEventFor<S extends AnalyticsSource> = {
  [E in AnalyticsEventName]: S extends AnalyticsEvents[E]["sources"][number] ? E : never;
}[AnalyticsEventName];

/** Parses the properties of an event. Returns null and the issue when they do not match the plan. */
export function checkAnalyticsProps<E extends AnalyticsEventName>(
  event: E,
  props: unknown,
): { ok: true; props: AnalyticsProps<E> } | { ok: false; issue: string } {
  const definition = analyticsEvents[event];
  if (!definition) return { ok: false, issue: `unknown event ${String(event)}` };
  const parsed = definition.props.strict().safeParse(props ?? {});
  if (parsed.success) return { ok: true, props: parsed.data as AnalyticsProps<E> };
  return { ok: false, issue: parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; ") };
}
